/**
 * Notifications Page
 * Version: 6.2.0
 * 
 * Full notifications management page
 */
import { useState } from 'react'
import { useNotifications } from '../hooks/useNotifications'
import { NotificationBell, NotificationPanel, ToastContainer } from '../components/Notifications'
import { NotificationSettings } from '../components/NotificationSettings'
import { NotificationCategory, NotificationPriority } from '../types/notifications'
import './NotificationsPage.css'

function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    filteredNotifications,
    settings,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    updateSettings,
    resetSettings,
    toastQueue,
    dismissToast,
  } = useNotifications()

  const [showPanel, setShowPanel] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'trade' | 'alert' | 'bot' | 'portfolio'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Filter by tab
  const tabCategories: Record<string, NotificationCategory[]> = {
    all: [],
    trade: [NotificationCategory.TRADE],
    alert: [NotificationCategory.ALERT],
    bot: [NotificationCategory.BOT],
    portfolio: [NotificationCategory.PORTFOLIO],
  }

  const displayNotifications = filteredNotifications.filter(n => {
    const categoryMatch = tabCategories[activeTab].length === 0 || 
      tabCategories[activeTab].includes(n.category)
    const searchMatch = !searchQuery || 
      n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.message.toLowerCase().includes(searchQuery.toLowerCase())
    return categoryMatch && searchMatch
  })

  // Demo: Add test notification
  const handleAddTestNotification = () => {
    addNotification({
      title: 'Test Notification',
      message: 'This is a test notification created for demo purposes',
      category: NotificationCategory.TRADE,
      priority: NotificationPriority.HIGH,
      data: { orderId: 'TEST-001', symbol: 'NIFTY', type: 'BUY', quantity: 50, price: 245, pnl: 1500, botName: 'Test Bot' },
    })
  }

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="notifications-header">
        <div className="header-left">
          <h1 className="page-title">🔔 Notifications</h1>
          <p className="page-subtitle">Manage your alerts and updates</p>
        </div>
        <div className="header-right">
          <button 
            className="settings-btn"
            onClick={() => setShowSettings(true)}
          >
            ⚙️ Settings
          </button>
          <div className="notification-bell-wrapper">
            <NotificationBell
              unreadCount={unreadCount}
              isOpen={showPanel}
              onClick={() => setShowPanel(!showPanel)}
            />
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{notifications.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-item highlight">
          <span className="stat-value">{unreadCount}</span>
          <span className="stat-label">Unread</span>
        </div>
        <div className="stat-item success">
          <span className="stat-value">
            {notifications.filter(n => n.status === 'read').length}
          </span>
          <span className="stat-label">Read</span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="search-filter-bar">
        <div className="search-input">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          {(['all', 'trade', 'alert', 'bot', 'portfolio'] as const).map((tab) => (
            <button
              key={tab}
              className={`filter-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-bar">
        <button className="action-btn" onClick={markAllAsRead}>
          ✓ Mark All Read
        </button>
        <button className="action-btn" onClick={clearAll}>
          🗑️ Clear All
        </button>
        <button className="action-btn primary" onClick={handleAddTestNotification}>
          + Add Test Notification
        </button>
      </div>

      {/* Notification List */}
      <div className="notification-list">
        {displayNotifications.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔕</span>
            <h3>No notifications</h3>
            <p>You're all caught up! New notifications will appear here.</p>
          </div>
        ) : (
          displayNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`notification-card ${notification.status === 'unread' ? 'unread' : ''} priority-${notification.priority}`}
            >
              <div className="card-icon">
                {notification.icon || '🔔'}
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h4 className="card-title">{notification.title}</h4>
                  <span className="card-time">
                    {notification.timestamp.toLocaleString()}
                  </span>
                </div>
                <p className="card-message">{notification.message}</p>
                {notification.data && (
                  <div className="card-tags">
                    {Object.entries(notification.data).slice(0, 4).map(([key, value]) => (
                      <span key={key} className="tag">
                        {key}: {typeof value === 'number' ? (key === 'pnl' || key === 'changeValue' ? `₹${value}` : value) : String(value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="card-actions">
                {notification.status === 'unread' && (
                  <button 
                    className="card-action-btn"
                    onClick={() => markAsRead(notification.id)}
                    title="Mark as read"
                  >
                    ✓
                  </button>
                )}
                <button 
                  className="card-action-btn dismiss"
                  onClick={() => dismissNotification(notification.id)}
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Components */}
      <NotificationPanel
        notifications={displayNotifications}
        isOpen={showPanel}
        onClose={() => setShowPanel(false)}
        onMarkRead={markAsRead}
        onMarkAllRead={markAllAsRead}
        onDismiss={dismissNotification}
        onClearAll={clearAll}
      />

      <NotificationSettings
        settings={settings}
        onUpdate={updateSettings}
        onReset={resetSettings}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      <ToastContainer
        toasts={toastQueue}
        onDismiss={dismissToast}
      />
    </div>
  )
}

export default NotificationsPage